import React, { useState, useEffect, useCallback, useRef } from 'react';
import BBALoader from '../../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Loader2,
  Trash2,
  Pencil,
  Upload,
  History,
  RotateCcw,
  Download,
  AlertTriangle,
  MoreVertical,
  CheckCircle,
  Plus,
  ChevronUp,
  ChevronDown,
  SmilePlus,
} from 'lucide-react';
import BackButton from '../../components/BackButton';
import Modal from '../../components/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  listClaudeSkills,
  uploadClaudeSkill,
  deleteClaudeSkill,
  updateClaudeSkillMetadata,
  updateClaudeSkillEmoji,
  getClaudeSkillSections,
  saveClaudeSkillSections,
  makeSkillSectionId,
  effectiveSectionId,
  saveClaudeSkillsOrder,
  uploadClaudeSkillVersion,
  fetchClaudeSkillVersionHistory,
  rollbackClaudeSkillToVersion,
} from '../../services/claudeSkillsService';
import { suggestNextVersion } from '../../services/hrCollectionsService';
import { downloadRemoteFile } from '../../services/technologyCommunityService';
import PortalBackground from '../../components/PortalBackground';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * CRM manager for Skills. Mirrors the Sales/HR collections look: pill
 * section tabs (user-created, with emoji + rename/reorder/delete), two-column
 * skill cards with a "Skill file" panel (new version / download / history),
 * kebab menu for edit and delete, reorder chevrons, and the shared emoji
 * picker for sections and skills. The portal Skills page shows the
 * same sections, emojis, and order.
 */
const ClaudeSkillsTemplate = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [sections, setSections] = useState([]);
  const [skills, setSkills] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuKey, setMenuKey] = useState(null);

  // Section (pill) add/rename modal — emoji lives on the draft until saved
  const [sectionModal, setSectionModal] = useState(null); // { mode: 'add' | 'rename', sectionId?, label, emoji }
  const [savingSection, setSavingSection] = useState(false);
  const [sectionError, setSectionError] = useState('');

  // Add-skill modal
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [skillFile, setSkillFile] = useState(null);
  const [addEmoji, setAddEmoji] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit details modal
  const [editRow, setEditRow] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSectionId, setEditSectionId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Emoji picker — targets the section modal draft ({ kind: 'section' }), the
  // add-skill draft ({ kind: 'add' }), or a saved skill ({ kind: 'item', row })
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [emojiSaving, setEmojiSaving] = useState(false);

  // New-version upload modal
  const [versionRow, setVersionRow] = useState(null);
  const [versionNumber, setVersionNumber] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [versionFile, setVersionFile] = useState(null);
  const [versionError, setVersionError] = useState('');
  const [versionUploading, setVersionUploading] = useState(false);

  // Version history modal
  const [historyRow, setHistoryRow] = useState(null);
  const [versions, setVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const userName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || 'BBA Team';

  const card = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const panel = `rounded-xl p-3.5 bg-gray-50 dark:bg-gray-800/60`;
  const chipGood =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  const iconBtn = `p-2 rounded-md border transition-colors ${
    darkMode
      ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
  }`;
  const chevronBtn = `p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
    darkMode
      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
      : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
  }`;
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [secs, rows] = await Promise.all([getClaudeSkillSections(), listClaudeSkills()]);
      setSections(secs);
      setSkills(rows);
      setActiveTab((current) =>
        secs.some((s) => s.id === current) ? current : secs[0]?.id || null
      );
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load skills.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // CRM sidebar search deep-links land here as ?highlight=<skillId> — switch
  // to the skill's section tab, then scroll to and briefly highlight its card.
  const [highlightId, setHighlightId] = useState(
    () => new URLSearchParams(window.location.search).get('highlight') || null
  );
  const highlightTabSetRef = useRef(false);
  useEffect(() => {
    if (!highlightId || loading) return undefined;
    const row = skills.find((r) => r.id === highlightId);
    if (!row) return undefined;
    if (!highlightTabSetRef.current) {
      highlightTabSetRef.current = true;
      setActiveTab(effectiveSectionId(row, sections));
    }
    const scroll = setTimeout(() => {
      document
        .querySelector(`[data-skill-card="${CSS.escape(highlightId)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const clear = setTimeout(() => setHighlightId(null), 3500);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
  }, [highlightId, loading, skills, sections]);

  const refreshSkills = async () => setSkills(await listClaudeSkills());

  const sectionSkillCount = (sectionId) =>
    skills.filter((r) => effectiveSectionId(r, sections) === sectionId).length;

  const activeSection = sections.find((s) => s.id === activeTab) || null;
  const sectionRows = skills.filter((r) => effectiveSectionId(r, sections) === activeTab);

  // ---- Sections ----
  const persistSections = async (next) => {
    await saveClaudeSkillSections(next);
    setSections(next);
  };

  const submitSection = async () => {
    const label = (sectionModal?.label || '').trim();
    if (!label) {
      setSectionError('Enter a section name.');
      return;
    }
    const clash = sections.some(
      (s) => s.id !== sectionModal.sectionId && s.label.toLowerCase() === label.toLowerCase()
    );
    if (clash) {
      setSectionError('A section with this name already exists.');
      return;
    }
    setSavingSection(true);
    setSectionError('');
    try {
      if (sectionModal.mode === 'rename') {
        await persistSections(
          sections.map((s) =>
            s.id === sectionModal.sectionId ? { ...s, label, emoji: sectionModal.emoji || '' } : s
          )
        );
      } else {
        const newSection = {
          id: makeSkillSectionId(label, sections),
          label,
          emoji: sectionModal.emoji || '',
        };
        await persistSections([...sections, newSection]);
        setActiveTab(newSection.id);
      }
      setSectionModal(null);
    } catch (err) {
      console.error('Section save failed:', err);
      setSectionError('Failed to save. Please try again.');
    } finally {
      setSavingSection(false);
    }
  };

  const moveSection = async (sectionId, direction) => {
    const idx = sections.findIndex((s) => s.id === sectionId);
    const target = idx + direction;
    if (idx === -1 || target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSections(next);
    try {
      await saveClaudeSkillSections(next);
    } catch (err) {
      console.error('Section reorder failed:', err);
      await load();
    }
  };

  const deleteSection = async () => {
    const section = sections.find((s) => s.id === sectionModal?.sectionId);
    if (!section) return;
    if (sectionSkillCount(section.id) > 0) return; // guarded in UI too
    if (sections.length <= 1) return;
    if (!window.confirm(`Delete the "${section.label}" section?`)) return;
    setSavingSection(true);
    try {
      const next = sections.filter((s) => s.id !== section.id);
      await persistSections(next);
      if (activeTab === section.id) setActiveTab(next[0]?.id || null);
      setSectionModal(null);
    } catch (err) {
      console.error('Section delete failed:', err);
      setSectionError('Failed to delete. Please try again.');
    } finally {
      setSavingSection(false);
    }
  };

  // ---- Emoji picking (section modal draft or skill card) ----
  const pickEmoji = async (emoji) => {
    if (!emojiTarget) return;
    if (emojiTarget.kind === 'section') {
      setSectionModal((m) => (m ? { ...m, emoji } : m));
      setEmojiTarget(null);
      return;
    }
    if (emojiTarget.kind === 'add') {
      setAddEmoji(emoji);
      setEmojiTarget(null);
      return;
    }
    setEmojiSaving(true);
    setError(null);
    try {
      await updateClaudeSkillEmoji({ docId: emojiTarget.row.id, emoji });
      setSkills((prev) =>
        prev.map((r) => (r.id === emojiTarget.row.id ? { ...r, emoji: emoji || null } : r))
      );
      setEmojiTarget(null);
    } catch (err) {
      console.error('Emoji save failed:', err);
      setError(err.message || 'Could not save the emoji.');
    } finally {
      setEmojiSaving(false);
    }
  };

  // ---- Add skill ----
  const openAdd = () => {
    setTitle('');
    setDescription('');
    setSkillFile(null);
    setAddEmoji('');
    setAddError('');
    setAddOpen(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) {
      setAddError('You must be signed in to upload.');
      return;
    }
    setSubmitting(true);
    setAddError('');
    try {
      await uploadClaudeSkill({
        title,
        description,
        skillFile,
        sectionId: activeTab,
        emoji: addEmoji,
        userId: currentUser.uid,
        authorName: userName,
        source: 'crm',
      });
      setAddOpen(false);
      await refreshSkills();
    } catch (err) {
      console.error(err);
      setAddError(err.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Edit details ----
  const openEdit = (row) => {
    setEditRow(row);
    setEditTitle(row.title || '');
    setEditDescription(row.description || '');
    setEditSectionId(effectiveSectionId(row, sections) || '');
  };

  const submitEdit = async () => {
    if (!editRow) return;
    const t = editTitle.trim();
    if (!t) return;
    setSavingEdit(true);
    setError(null);
    try {
      await updateClaudeSkillMetadata({
        docId: editRow.id,
        title: t,
        description: editDescription,
        sectionId: editSectionId || undefined,
      });
      if (editSectionId && editSectionId !== activeTab) setActiveTab(editSectionId);
      setEditRow(null);
      await refreshSkills();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Save failed.');
    } finally {
      setSavingEdit(false);
    }
  };

  // ---- Reorder (display order here and on the portal) ----
  const moveSkill = async (row, direction) => {
    const idx = sectionRows.findIndex((r) => r.id === row.id);
    const target = idx + direction;
    if (idx === -1 || target < 0 || target >= sectionRows.length) return;
    const next = [...sectionRows];
    [next[idx], next[target]] = [next[target], next[idx]];
    const orderById = new Map(next.map((r, i) => [r.id, i]));
    setSkills((prev) =>
      prev.map((r) => (orderById.has(r.id) ? { ...r, sortOrder: orderById.get(r.id) } : r))
    );
    try {
      await saveClaudeSkillsOrder(next.map((r) => r.id));
    } catch (err) {
      console.error('Reorder failed:', err);
      await refreshSkills();
    }
  };

  // ---- New version upload ----
  const openVersionModal = (row) => {
    setVersionRow(row);
    setVersionNumber(suggestNextVersion(row.version));
    setVersionNotes('');
    setVersionFile(null);
    setVersionError('');
  };

  const submitVersion = async () => {
    if (!versionRow) return;
    if (!versionFile || !versionNumber.trim()) {
      setVersionError('Select a .skill file and enter a version number.');
      return;
    }
    setVersionUploading(true);
    setVersionError('');
    try {
      await uploadClaudeSkillVersion({
        docId: versionRow.id,
        file: versionFile,
        version: versionNumber,
        notes: versionNotes,
        user: { uid: currentUser?.uid, name: userName },
      });
      setVersionRow(null);
      await refreshSkills();
    } catch (err) {
      console.error('Version upload failed:', err);
      setVersionError(err.message || 'Upload failed. Please try again.');
    } finally {
      setVersionUploading(false);
    }
  };

  // ---- Version history ----
  const openHistory = async (row) => {
    setHistoryRow(row);
    setVersions([]);
    setHistoryLoading(true);
    try {
      setVersions(await fetchClaudeSkillVersionHistory(row.id));
    } catch (err) {
      console.error('History load failed:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRestore = async (versionId) => {
    if (!historyRow) return;
    setHistoryLoading(true);
    try {
      await rollbackClaudeSkillToVersion(historyRow.id, versionId, {
        uid: currentUser?.uid,
        name: userName,
      });
      setVersions(await fetchClaudeSkillVersionHistory(historyRow.id));
      await refreshSkills();
    } catch (err) {
      console.error('Restore failed:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? Its file and version history are removed.`)) return;
    setError(null);
    try {
      await deleteClaudeSkill({ docId: row.id });
      await refreshSkills();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Delete failed.');
    }
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative w-full max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <BackButton onClick={() => navigate('/crm/templates')} />
        <h1 className="text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white">
          Skills
        </h1>
        <div className="w-10" aria-hidden />
      </div>

      {/* Section tabs (collection-style pills; pencil on the active pill opens the section modal) */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {sections.map((section) => {
          const isActive = activeTab === section.id;
          return (
            <div
              key={section.id}
              className={`inline-flex items-center rounded-full text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-red-600 text-white'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <button
                type="button"
                onClick={() => setActiveTab(section.id)}
                className={`px-4 py-2 ${isActive ? 'pr-1' : ''}`}
              >
                {section.emoji && <span className="mr-1.5">{section.emoji}</span>}
                {section.label}
              </button>
              {isActive && (
                <button
                  type="button"
                  onClick={() => {
                    setSectionError('');
                    setSectionModal({
                      mode: 'rename',
                      sectionId: section.id,
                      label: section.label,
                      emoji: section.emoji || '',
                    });
                  }}
                  className="pl-1 pr-3 py-2 text-white/80 hover:text-white"
                  title="Rename section"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setSectionError('');
            setSectionModal({ mode: 'add', label: '', emoji: '' });
          }}
          className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium border border-dashed transition-colors ${
            darkMode
              ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
              : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
          }`}
          title="Add a new section"
        >
          <Plus className="w-4 h-4" /> Add section
        </button>
      </div>

      {error && (
        <div
          className={`mb-4 rounded-md border px-4 py-3 text-sm ${
            darkMode
              ? 'border-red-800 bg-red-950/50 text-red-200'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <BBALoader size={40} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {sectionRows.map((row, rowIndex) => {
            const f = row.skillFile || {};
            const menuOpen = menuKey === row.id;
            return (
              <div
                key={row.id}
                data-skill-card={row.id}
                className={`${card} rounded-2xl shadow-sm p-5 flex flex-col ${
                  highlightId === row.id ? 'ring-2 ring-red-400 dark:ring-red-500' : ''
                }`}
              >
                {/* Header: emoji tile + title/description + reorder + kebab */}
                <div className="flex items-start gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setEmojiTarget({ kind: 'item', row })}
                    title="Choose an emoji for this skill"
                    className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {row.emoji ? (
                      <span className="text-2xl leading-none">{row.emoji}</span>
                    ) : (
                      <Sparkles
                        className={`h-6 w-6 ${darkMode ? 'text-red-400' : 'text-red-600'}`}
                      />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                      {row.title}
                    </h3>
                    {row.description && (
                      <p className={`text-sm ${subtle} mt-0.5 line-clamp-2`}>{row.description}</p>
                    )}
                  </div>
                  <div className="relative shrink-0 flex items-center">
                    <button
                      type="button"
                      onClick={() => moveSkill(row, -1)}
                      disabled={rowIndex === 0}
                      className={chevronBtn}
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveSkill(row, 1)}
                      disabled={rowIndex === sectionRows.length - 1}
                      className={chevronBtn}
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setMenuKey(menuOpen ? null : row.id)}
                      className={`p-1.5 rounded-md ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {menuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuKey(null)} />
                        <div
                          className={`absolute right-0 top-8 z-20 w-44 rounded-lg shadow-lg border py-1 ${
                            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMenuKey(null);
                              openEdit(row);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                              darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit details
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuKey(null);
                              handleDelete(row);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                              darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete skill
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Skill file — the .skill clients download from the portal */}
                <div className={panel}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${subtle}`}>
                      Skill file
                    </span>
                    <span className={chipGood}>
                      <CheckCircle className="w-3 h-3" /> v{row.version || '1.0'}
                    </span>
                  </div>
                  <p className={`text-xs ${subtle} mb-3`}>
                    .SKILL
                    {formatSize(f.size) ? ` · ${formatSize(f.size)}` : ''}
                    {formatDate(row.updatedAt || row.createdAt)
                      ? ` · Updated ${formatDate(row.updatedAt || row.createdAt)}`
                      : ''}
                    {row.authorName ? ` · ${row.authorName}` : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openVersionModal(row)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md text-white bg-red-600 hover:bg-red-700"
                    >
                      <Upload className="w-3.5 h-3.5" /> New version
                    </button>
                    {f.downloadURL && (
                      <button
                        type="button"
                        onClick={() => downloadRemoteFile(f.downloadURL, f.fileName || 'download.skill')}
                        className={iconBtn}
                        title="Download .skill"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openHistory(row)}
                      className={iconBtn}
                      title="Version history"
                    >
                      <History className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add skill tile */}
          <button
            type="button"
            onClick={openAdd}
            className={`rounded-lg border-2 border-dashed p-5 flex flex-col items-center justify-center gap-2 min-h-[180px] transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
            }`}
          >
            <Plus className="w-6 h-6" />
            <span className="text-sm font-medium">Add skill</span>
          </button>
        </div>
      )}

      {/* Add skill modal */}
      <Modal isOpen={addOpen} onClose={() => !submitting && setAddOpen(false)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Add skill
        </h2>
        <p className={`text-sm ${subtle} mb-4 text-center`}>
          Adds a new skill to “{activeSection?.label}”.
        </p>
        <form onSubmit={submitAdd} className="space-y-4">
          <div>
            <label htmlFor="skill-title" className={labelClass}>
              Title
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setEmojiTarget({ kind: 'add' })}
                disabled={submitting}
                title="Choose an emoji for this skill"
                className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {addEmoji ? (
                  <span className="text-2xl leading-none">{addEmoji}</span>
                ) : (
                  <SmilePlus className={`w-5 h-5 ${subtle}`} />
                )}
              </button>
              <input
                id="skill-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Estimate Builder"
                className={inputClass}
                disabled={submitting}
                autoFocus
              />
            </div>
          </div>
          <div>
            <label htmlFor="skill-description" className={labelClass}>
              Description
            </label>
            <textarea
              id="skill-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this skill does"
              rows={2}
              className={inputClass}
              disabled={submitting}
            />
          </div>
          <div>
            <span className={labelClass}>.skill file</span>
            <label
              className={`mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-red-500/60 bg-gray-900/30'
                  : 'border-gray-300 hover:border-red-400 bg-gray-50/50'
              } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <input
                type="file"
                accept=".skill"
                className="sr-only"
                disabled={submitting}
                onChange={(e) => {
                  setSkillFile(e.target.files?.[0] || null);
                  setAddError('');
                }}
              />
              <Upload className={`mb-2 h-7 w-7 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {skillFile ? skillFile.name : 'Click to choose a .skill file'}
              </span>
              <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">Up to 15MB</span>
            </label>
          </div>

          {addError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {addError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              disabled={submitting}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !skillFile || !title.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Upload
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit details modal */}
      <Modal isOpen={!!editRow} onClose={() => !savingEdit && setEditRow(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Edit details</h2>
        <label className={labelClass}>Title</label>
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className={`${inputClass} mb-4`}
          disabled={savingEdit}
        />
        <label className={labelClass}>Section</label>
        <select
          value={editSectionId}
          onChange={(e) => setEditSectionId(e.target.value)}
          className={`${inputClass} mb-4`}
          disabled={savingEdit}
        >
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.emoji ? `${s.emoji} ` : ''}{s.label}
            </option>
          ))}
        </select>
        <label className={labelClass}>Description</label>
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          rows={4}
          className={`${inputClass} mb-4`}
          disabled={savingEdit}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setEditRow(null)}
            disabled={savingEdit}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitEdit}
            disabled={savingEdit || !editTitle.trim()}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
          >
            {savingEdit ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* Section add/rename modal */}
      <Modal isOpen={!!sectionModal} onClose={() => !savingSection && setSectionModal(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {sectionModal?.mode === 'rename' ? 'Rename section' : 'Add section'}
        </h2>
        <label className={labelClass}>Section name</label>
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => setEmojiTarget({ kind: 'section' })}
            title="Choose an emoji for this section"
            className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {sectionModal?.emoji ? (
              <span className="text-2xl leading-none">{sectionModal.emoji}</span>
            ) : (
              <SmilePlus className={`w-5 h-5 ${subtle}`} />
            )}
          </button>
          <input
            type="text"
            value={sectionModal?.label || ''}
            onChange={(e) => setSectionModal((m) => ({ ...m, label: e.target.value }))}
            placeholder="e.g. Estimating"
            autoFocus
            className={`flex-1 px-3 py-2 border rounded-md ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
            }`}
          />
        </div>
        {sectionModal?.mode === 'rename' && (() => {
          const idx = sections.findIndex((s) => s.id === sectionModal.sectionId);
          const posBtn = `p-1.5 rounded-md border transition-colors disabled:opacity-25 disabled:cursor-default ${
            darkMode
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white disabled:hover:bg-transparent disabled:hover:text-gray-300'
              : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:hover:bg-transparent disabled:hover:text-gray-600'
          }`;
          return (
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Position <span className={`font-normal ${subtle}`}>({idx + 1} of {sections.length})</span>
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => moveSection(sectionModal.sectionId, -1)}
                  disabled={savingSection || idx <= 0}
                  className={posBtn}
                  title="Move section up"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveSection(sectionModal.sectionId, 1)}
                  disabled={savingSection || idx === sections.length - 1}
                  className={posBtn}
                  title="Move section down"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })()}
        {sectionError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {sectionError}
          </p>
        )}
        <div className="flex items-center justify-between gap-2">
          <div>
            {sectionModal?.mode === 'rename' &&
              (sections.length > 1 && sectionSkillCount(sectionModal.sectionId) === 0 ? (
                <button
                  onClick={deleteSection}
                  disabled={savingSection}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="w-4 h-4" /> Delete section
                </button>
              ) : (
                <p className={`text-xs ${subtle}`}>
                  {sections.length <= 1
                    ? 'At least one section is required.'
                    : 'Remove all skills to delete this section.'}
                </p>
              ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSectionModal(null)}
              disabled={savingSection}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={submitSection}
              disabled={savingSection}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
            >
              {savingSection ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* New version modal */}
      <Modal isOpen={!!versionRow} onClose={() => !versionUploading && setVersionRow(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          Upload new version
        </h2>
        <p className={`text-sm mb-4 ${subtle}`}>{versionRow?.title}</p>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Version number <span className={`font-normal ${subtle}`}>(auto-suggested)</span>
        </label>
        <input
          type="text"
          value={versionNumber}
          onChange={(e) => setVersionNumber(e.target.value)}
          placeholder="e.g. 1.1, 2.0"
          disabled={versionUploading}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Change notes (optional)
        </label>
        <textarea
          value={versionNotes}
          onChange={(e) => setVersionNotes(e.target.value)}
          rows={2}
          placeholder="What changed in this version?"
          disabled={versionUploading}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          .skill file
        </label>
        <input
          type="file"
          accept=".skill"
          disabled={versionUploading}
          onChange={(e) => {
            setVersionFile(e.target.files?.[0] || null);
            setVersionError('');
          }}
          className={`w-full text-sm mb-2 ${subtle}`}
        />
        <p className={`text-xs mb-4 ${subtle}`}>
          .skill up to 15MB. The previous file stays available in the version history.
        </p>

        {versionError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {versionError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setVersionRow(null)}
            disabled={versionUploading}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitVersion}
            disabled={versionUploading || !versionFile || !versionNumber.trim()}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {versionUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </Modal>

      {/* Version history modal */}
      <Modal isOpen={!!historyRow} onClose={() => setHistoryRow(null)} maxWidth="2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Version history</h2>
        <p className={`text-sm mb-4 ${subtle}`}>{historyRow?.title}</p>

        {historyLoading ? (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-8">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : versions.length === 0 ? (
          <p className={`py-6 text-center text-sm ${subtle}`}>No version history yet.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {versions.map((v, idx) => (
              <div
                key={v.id}
                className={`flex items-center justify-between p-3 rounded-md ${
                  darkMode ? 'bg-gray-700/60' : 'bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      v{v.version}
                    </span>
                    {idx === 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        Current
                      </span>
                    )}
                  </div>
                  <p className={`text-xs truncate ${subtle}`}>
                    {v.uploadedByName} · {formatDate(v.uploadedAt) || '—'}
                    {v.notes ? ` · ${v.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <button
                    onClick={() => downloadRemoteFile(v.downloadURL, v.fileName || 'download.skill')}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                    title="Download this version"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {idx !== 0 && (
                    <button
                      onClick={() => handleRestore(v.id)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                      title="Roll back to this version"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={() => setHistoryRow(null)}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Emoji picker (sections and skill cards; stacks above the section modal) */}
      <Modal isOpen={!!emojiTarget} onClose={() => !emojiSaving && setEmojiTarget(null)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an emoji
        </h2>
        <p className={`text-sm mb-4 text-center ${subtle}`}>
          {emojiTarget?.kind === 'section'
            ? sectionModal?.label || 'New section'
            : emojiTarget?.kind === 'add'
            ? title.trim() || 'New skill'
            : emojiTarget?.row?.title}
        </p>
        <div className="flex justify-center">
          {emojiTarget && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <BBALoader size={32} />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => !emojiSaving && pickEmoji(emojiData.emoji)}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {(emojiTarget?.kind === 'section'
            ? !!sectionModal?.emoji
            : emojiTarget?.kind === 'add'
            ? !!addEmoji
            : !!emojiTarget?.row?.emoji) ? (
            <button
              onClick={() => pickEmoji('')}
              disabled={emojiSaving}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Use default
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setEmojiTarget(null)}
            disabled={emojiSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {emojiSaving ? 'Saving…' : 'Cancel'}
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default ClaudeSkillsTemplate;
